import { useMatch, useNavigate, useSearch } from '@tanstack/react-router';
import { type ReactNode, useEffect } from 'react';
import type { SheetData } from '~/modules/common/sheeter/use-sheeter';
import { useSheeter } from '~/modules/common/sheeter/use-sheeter';
import { fallbackContentRef } from '~/utils/fallback-content-ref';

type SheetOptions = Omit<SheetData, 'id' | 'triggerRef' | 'onClose'>;

interface OverlayConfig<TOptions> {
  /** The search param key to watch (e.g., 'userSheetId') */
  searchParamKey: string;
  /** Render overlay content. Receives the search param value and org ID from URL context. */
  renderContent: (id: string, organizationId: string | undefined) => ReactNode;
  /** Called after close (not during cleanup). Receives the search param value. */
  onAfterClose?: (id: string) => void;
  options: TOptions;
}

type UseUrlSheetConfig = OverlayConfig<SheetOptions>;

/** Clears the search params by replacing the current history entry. */
function useCloseOverlay(searchParamKey: string, additionalParamKeys: string[] = []) {
  const navigate = useNavigate();
  const paramsToRemove = [searchParamKey, ...additionalParamKeys];

  return () => {
    navigate({
      to: '.',
      replace: true,
      resetScroll: false,
      search: (prev) => {
        const next: Record<string, unknown> = { ...prev };
        for (const key of paramsToRemove) next[key] = undefined;
        return next;
      },
    });
  };
}

/**
 * Manages a URL-driven sheet overlay.
 * Opens a sheet when the search param is present, closes it when removed.
 */
export function useUrlSheet(config: UseUrlSheetConfig) {
  const { searchParamKey, renderContent, onAfterClose, options } = config;

  // Primitive selects, so writes to other search keys don't re-render the host.
  const value = useSearch({ strict: false, select: (s) => (s as Record<string, string | undefined>)[searchParamKey] ?? null });
  const organizationId = useMatch({ from: '/_app/$tenantId/$organizationSlug', shouldThrow: false, select: (m) => m.context.organization?.id });
  const close = useCloseOverlay(searchParamKey);

  useEffect(() => {
    if (!value) return;

    const id = `${searchParamKey}-${value}`;
    if (useSheeter.getState().get(id)) return;

    const handleClose = (isCleanup?: boolean) => {
      if (!isCleanup) {
        onAfterClose?.(value);
        close();
      }
    };

    queueMicrotask(() => {
      useSheeter.getState().create(renderContent(value, organizationId), { id, triggerRef: fallbackContentRef, onClose: handleClose, ...options });
    });

    return () => {
      useSheeter.getState().remove(id, { isCleanup: true });
    };
  }, [value, organizationId]);
}
